import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { fetchTopic } from "@/lib/api";
import { absoluteUrl } from "@/lib/seo-config";
import { BOOKING_URL } from "@/lib/constants";

export default function TopicPage() {
  const { topicSlug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
    fetchTopic(topicSlug)
      .then(setData)
      .catch(() => setError(true));
  }, [topicSlug]);

  if (error) {
    return (
      <main data-testid="topic-not-found" className="bg-white py-32 text-center px-6">
        <SEO title="Topic not found" path={`/topics/${topicSlug}`} noIndex />
        <h1 className="text-3xl font-bold text-[#080808]">Topic not found</h1>
        <p className="text-[#5A5F6A] mt-4">
          Head back to the{" "}
          <Link to="/blog" className="ash-link-underline">
            journal
          </Link>{" "}
          and we&apos;ll find the right entry.
        </p>
      </main>
    );
  }
  if (!data) {
    return <main className="bg-white py-32" data-testid="topic-loading" />;
  }

  return (
    <main data-testid="topic-page" className="bg-white">
      <SEO
        title={`${data.category} — Articles & insight`}
        description={`Every Ashlan Clinic journal entry on ${data.category}. ${data.count} article${data.count === 1 ? "" : "s"} written by our Therapists.`}
        path={`/topics/${data.slug}`}
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${data.category} — Ashlan Clinic Journal`,
            url: absoluteUrl(`/topics/${data.slug}`),
            isPartOf: { "@type": "Blog", url: absoluteUrl("/blog") },
            mainEntity: {
              "@type": "ItemList",
              itemListElement: data.posts.map((p, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: absoluteUrl(`/blog/${p.slug}`),
                name: p.title,
              })),
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: absoluteUrl("/") },
              { "@type": "ListItem", position: 2, name: "Journal", item: absoluteUrl("/blog") },
              { "@type": "ListItem", position: 3, name: data.category, item: absoluteUrl(`/topics/${data.slug}`) },
            ],
          },
        ]}
      />

      {/* Header */}
      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <Link to="/blog" className="text-sm text-[#3A7A8C] inline-flex items-center gap-1.5 hover:underline">
            <ArrowLeft size={14} /> All journal entries
          </Link>
          <span className="ash-eyebrow mt-6 block">Topic</span>
          <h1 className="mt-3 text-4xl md:text-6xl font-bold text-[#080808] tracking-tight leading-[1.05]">
            {data.category}
          </h1>
          <p className="mt-4 text-[#3C4048] max-w-2xl text-base md:text-lg leading-relaxed">
            {data.count} article{data.count === 1 ? "" : "s"} from the Ashlan Clinic Journal on {data.category}.
          </p>
        </div>
      </section>

      {/* Posts grid */}
      <section className="bg-[#F5F5F5] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {data.posts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`topic-post-${p.slug}`}
                className="group bg-white border border-[#e5e7eb] flex flex-col transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="aspect-[16/10] overflow-hidden bg-[#F5F5F5]">
                  <img
                    src={p.featured_image_url}
                    alt={p.featured_image_description || p.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-6 flex flex-col gap-3">
                  <div className="text-xs uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">
                    {p.category}
                  </div>
                  <h3 className="text-lg font-bold text-[#080808] leading-tight">{p.title}</h3>
                  {p.excerpt && (
                    <p className="text-sm text-[#3C4048] leading-relaxed line-clamp-3">{p.excerpt}</p>
                  )}
                  <span className="ash-link-underline mt-2">
                    Read <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#080808] tracking-tight">
            Ready to discuss your care plan?
          </h2>
          <p className="mt-4 text-[#3C4048] text-base md:text-lg leading-relaxed">
            Speak with one of our Therapists about a treatment approach tailored to you.
          </p>
          <a href={BOOKING_URL} className="ash-btn-primary mt-8 inline-block" data-testid="topic-book-cta">
            Book a Session
          </a>
        </div>
      </section>
    </main>
  );
}
